import { useMapboxLayerUtils } from '@/components/maps/composables/useMapboxLayerUtils'
import useMap from '@/composables/scopedStores/map/useMap'
import { baseMapStyleUrls, BasemapTweaks, defaultBaseMap } from '@/config/map'
import {
  iconConfig,
  SimpleLayerSpecification,
  TBaseMapStyle,
  TBasemapTweak,
  TStyleLoadHandler
} from '@lifk/types'
import { ExpressionSpecification } from 'mapbox-gl'
import {
  computed,
  Directive,
  DirectiveBinding,
  inject,
  InjectionKey,
  provide,
  Ref,
  ref,
  watch
} from 'vue'

type TBaseMapStore = {
  baseMapStyle: Ref<TBaseMapStyle>
  baseMapStyleUrl: Ref<string>
  toggleBaseMapStyle: () => void
  setHiddenBaseMapTweaks: (toHide: TBasemapTweak[]) => void
  vStyleLoad: Directive<HTMLElement, TStyleLoadHandler>
}
export const BaseMapStoreKey = Symbol() as InjectionKey<TBaseMapStore>

export const provideBaseMap = () => {
  const map = useMap()
  const { getLayoutProperty, setLayoutProperty } = useMapboxLayerUtils()

  /*
    State
  */
  const baseMapStyle = ref<TBaseMapStyle>(defaultBaseMap)
  const baseMapStyleUrl = computed(() => baseMapStyleUrls[baseMapStyle.value])

  // mapbox StyleSpecification type is excessively deep/infinite, use `any` instead
  const styleSpec = ref<any | undefined>(map.getStyle() ?? undefined)

  const hiddenBaseMapTweaks = ref<TBasemapTweak[]>([])
  const styleLoadHandlers = ref<TStyleLoadHandler[]>([])

  /*
    Setters
  */
  const toggleBaseMapStyle = () => {
    baseMapStyle.value = baseMapStyle.value === 'light' ? 'satellite' : 'light'
  }

  const setHiddenBaseMapTweaks = (toHide: TBasemapTweak[]) => {
    hiddenBaseMapTweaks.value = toHide
  }

  const addEvent = (
    _el: HTMLElement,
    binding: DirectiveBinding<TStyleLoadHandler>
  ) => {
    const handler = binding.value
    styleLoadHandlers.value.push(handler)
  }

  const removeEvent = (
    _el: HTMLElement,
    binding: DirectiveBinding<TStyleLoadHandler>
  ) => {
    const handler = binding.value
    styleLoadHandlers.value = styleLoadHandlers.value.filter(
      (h) => h !== handler
    )
  }

  const vStyleLoad: Directive<HTMLElement, TStyleLoadHandler> = {
    mounted: addEvent,
    unmounted: removeEvent
  }

  /*
    Watchers / Apply state
  */
  const updateBasemapLayersVisibility = (
    tweaks: TBasemapTweak[],
    visible: boolean,
    styleLayers: SimpleLayerSpecification[]
  ) => {
    const layerIds = styleLayers
      .filter((layer) =>
        // Find layers that match any one of the active tweaks
        tweaks.some((tweak) => BasemapTweaks[tweak]?.match(layer))
      )
      .map(({ id }) => id)

    layerIds.forEach((id) => {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none')
      }
    })
  }

  const applyBaseMapTweaks = (
    nextHidden: TBasemapTweak[],
    prevHidden: TBasemapTweak[]
  ) => {
    // Update layer visibilities
    const toShow = prevHidden.filter((prev) => !nextHidden.includes(prev))
    const toHide = nextHidden.filter((next) => !prevHidden.includes(next))
    const styleLayers: SimpleLayerSpecification[] = (styleSpec.value?.layers ??
      []) as SimpleLayerSpecification[]

    updateBasemapLayersVisibility(toShow, true, styleLayers)
    updateBasemapLayersVisibility(toHide, false, styleLayers)

    // Enable/Disable terrain exaggeration (only set if there is a change)
    const enableTerrain = toShow.includes('Disable Terrain Exaggeration')
    const disableTerrain = toHide.includes('Disable Terrain Exaggeration')
    if (enableTerrain || disableTerrain) {
      map.setTerrain({ exaggeration: enableTerrain ? 1.5 : 0 } as any)
    }
  }

  watch(hiddenBaseMapTweaks, applyBaseMapTweaks)

  watch(baseMapStyleUrl, (newVal, oldVal) => {
    if (newVal !== oldVal) {
      // Note: erases all dynamic layers
      map.setStyle(baseMapStyleUrl.value, {
        diff: false
        // config: { basemap: { 'fill-extrusion-opacity': 0.3 } }
      } as any)
    }
  })

  // Replaces Mapbox POI icons with custom icons
  const replaceMapboxIcons = async (): Promise<void> => {
    try {
      const loadPromises = Object.entries(iconConfig).map(
        ([makiValue, config]) => {
          const iconPath = config[baseMapStyle.value]
          const imageId = `custom-${makiValue}-icon-${baseMapStyle.value}`

          return new Promise<{
            makiValue: string
            imageId: string
            layer: string
          }>((resolve, reject) => {
            map.loadImage(iconPath ?? '', (error, image) => {
              if (error) {
                console.warn(`Error loading icon for "${makiValue}":`, error)
                reject(error)
                return
              }

              if (image) {
                if (!map.hasImage(imageId)) {
                  map.addImage(imageId, image)
                }
                resolve({ makiValue, imageId, layer: config.layer })
              } else {
                reject(new Error(`Failed to load image for ${makiValue}`))
              }
            })
          })
        }
      )

      const loadedIcons = await Promise.allSettled(loadPromises)

      const successfulLoads = loadedIcons
        .filter(
          (
            result
          ): result is PromiseFulfilledResult<{
            makiValue: string
            imageId: string
            layer: string
          }> => result.status === 'fulfilled'
        )
        .map((result) => result.value)

      if (successfulLoads.length === 0) {
        console.warn('No icons loaded successfully')
        return
      }

      // Group icons by target layer
      const iconsByLayer: Record<
        string,
        { makiValue: string; imageId: string }[]
      > = {}
      for (const { layer, makiValue, imageId } of successfulLoads) {
        if (!iconsByLayer[layer]) iconsByLayer[layer] = []
        iconsByLayer[layer].push({ makiValue, imageId })
      }

      // Apply custom icons per layer
      for (const [layerName, icons] of Object.entries(iconsByLayer)) {
        const currentIconImage = getLayoutProperty(layerName, 'icon-image')
        const caseExpression: ExpressionSpecification = ['case']

        icons.forEach(({ makiValue, imageId }) => {
          caseExpression.push(['==', ['get', 'maki'], makiValue])
          caseExpression.push(imageId)
        })

        // Fallback
        caseExpression.push(currentIconImage || ['get', 'maki'])

        setLayoutProperty(layerName, 'icon-image', caseExpression)

        console.debug(
          `✅ Icons applied to layer "${layerName}": ${icons.map((i) => i.makiValue).join(', ')}`
        )
      }
    } catch (error) {
      console.error('Error in replaceMapboxIcons:', error)
    }
  }

  map.on('style.load', () => {
    styleSpec.value = map.getStyle() ?? undefined
    applyBaseMapTweaks(hiddenBaseMapTweaks.value, [])

    styleLoadHandlers.value.forEach((handler) => {
      handler()
    })

    if (baseMapStyle.value === 'satellite') {
      replaceMapboxIcons()
    }
  })

  /*
    Provide
  */
  // For use by children of MapContext.vue
  provide(BaseMapStoreKey, {
    baseMapStyle,
    baseMapStyleUrl,
    toggleBaseMapStyle,
    setHiddenBaseMapTweaks,
    vStyleLoad
  })
}

// Use in scenarios that may exist outside of Map context (ie- UIBanner.vue)
export function useOptionalBaseMap(): TBaseMapStore | undefined {
  // undefined default value suppresses console warnings
  return inject(BaseMapStoreKey, undefined)
}

// Use for scenarios that are guaranteed to exist within Map context (most cases)
export default function useBaseMap(): TBaseMapStore {
  return useOptionalBaseMap()!
}
